import { Fragment, useContext, useEffect, useState } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import useTranslation from 'next-translate/useTranslation'
import DropdownItemWithArrowRedesign, {
  DropdownItemWithArrowSide
} from '@/components/molecules/dropdownItemWithArrowRedesign'
import { FilterModel } from 'types/filterModel'

import { FilterQueryItem } from 'types/filterQueryItem'
import { Small } from '@/components/atoms/typography'
import styled from 'styled-components'
import {
  useActiveFilterDispatch,
  useActiveFiltersCount,
  useFilter
} from '@/providers/filterProvider/filtersProvider'
import { StoreContext } from '@/providers/storeProvider'
import dynamic from 'next/dynamic'
import { ActiveFilterItem } from '@/types/activeFiltersType'
import { IconButton } from '@/components/molecules/iconButtons'
import { ArrowUpDownIconDefault } from '../../atoms/icons/ArrowUpDownIconDefault'
import { FilterIconRedesignDefault } from '@/components/atoms/icons/FilterIconRedesignDefault'
import { ResetIconDefault } from '../../atoms/icons/ResetIconDefault'
import { FilterCheckboxRedesign } from '@/components/molecules/filter'

const DropDownGridItem = dynamic(() => import('./dropDownGridItem'), {
  ssr: false
})
const DropdownMenuItemsGrid = dynamic(() => import('./dropdownMenuItemsGrid'), {
  ssr: false
})
const ManufacturerDesktopFilter = dynamic(
  () => import('@/components/organisms/filter/manufacturerDesktopFilter'),
  { ssr: false }
)
const PriceFilterRedesign = dynamic(
  () => import('@/components/organisms/filter/priceFilterRedesign'),
  { ssr: false }
)

type DesktopFilterProps = {
  filter: FilterModel
  showInStorage: boolean
  showInSale: boolean
  isParfumCampaign: boolean
} & Rest

const DesktopFilter = ({
  filter,
  showInStorage,
  showInSale,
  isParfumCampaign,
  ...rest
}: DesktopFilterProps): JSX.Element => {
  const { t } = useTranslation('common')
  const { isFrogies, isAliatic } = useContext(StoreContext)
  const {
    genders,
    categories,
    sizes,
    manufacturers,
    colors,
    materials,
    priceRange,
    sortTypes,
    tags,
    patterns,
    hasInStock,
    hasInMainWarehouse
  } = filter || {}
  const { activeFilters } = useFilter()
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null)
  const activeFilterscount = useActiveFiltersCount()

  const {
    toggleFilterAndSetUrl,
    discardAllFilter,
    isInSizeFilters,
    isInManufacturerFilters,
    isInColorFilters,
    isInPatternFilters,
    isInMaterialFilters,
    isInGenderFilters,
    isInCategoryFilters,
    isInTagFilters,
    setFiltersForType
  } = useActiveFilterDispatch()

  const onDiscardAllFilter =
    (getParamType: FilterQueryItem, activeItems: ActiveFilterItem[]) => () => {
      activeItems.forEach((activeItem) => {
        toggleFilterAndSetUrl(
          activeItem.id,
          activeItem.englishName,
          getParamType
        )
      })
      discardAllFilter(getParamType)
    }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const dropdowns = document.querySelectorAll('[data-dropdown-wrapper]')
      let clickedInside = false
      dropdowns.forEach((node) => {
        if (node.contains(event.target as Node)) {
          clickedInside = true
        }
      })
      if (!clickedInside) {
        setOpenDropdownKey(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const allFilters = [
    (sortTypes?.length ?? 0) > 0 && (
      <DropdownItemWithArrowRedesign
        key="sortTypes"
        dropdownKey="sortTypes"
        filterType={FilterQueryItem.SORT}
        isOpen={openDropdownKey === 'sortTypes'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'sortTypes' ? null : 'sortTypes'
          )
        }
        Icon={ArrowUpDownIconDefault}
        side={DropdownItemWithArrowSide.right}
        title={
          activeFilters.sort?.title
            ? activeFilters.sort.title
            : t('Category.Filter.sort')
        }
        data-testid="filterSort"
      >
        {sortTypes.map(({ title, id }) => (
          <SortItem
            isSelected={id === activeFilters.sort?.id}
            key={id || title}
            onClick={() => {
              toggleFilterAndSetUrl(id, '', FilterQueryItem.SORT)
              setOpenDropdownKey(null)
            }}
            data-testid={id}
          >
            {title}
          </SortItem>
        ))}
      </DropdownItemWithArrowRedesign>
    ),

    showInSale ? (
      <FilterCheckboxRedesign filterType={FilterQueryItem.IN_SALE} />
    ) : null,

    showInStorage && !isFrogies && !isAliatic && hasInStock && (
      <FilterCheckboxRedesign filterType={FilterQueryItem.STOCK} />
    ),

    showInStorage && !isFrogies && !isAliatic && hasInMainWarehouse && (
      <FilterCheckboxRedesign filterType={FilterQueryItem.MAIN_WAREHOUSE} />
    ),

    genders && genders.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="genders"
        dropdownKey="genders"
        filterType={FilterQueryItem.GENDER}
        isOpen={openDropdownKey === 'genders'}
        onToggle={() =>
          setOpenDropdownKey((prev) => (prev === 'genders' ? null : 'genders'))
        }
        title={t('Category.Filter.gender')}
        data-testid="filterGender"
      >
        <DropdownMenuItemsGrid>
          <DropDownGridItem
            key="gendersgrid"
            typeDropDownItem={FilterQueryItem.GENDER}
            onDiscard={onDiscardAllFilter(
              FilterQueryItem.GENDER,
              activeFilters.genders
            )}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={genders.map(({ title, items }) => ({
              title,
              items:
                items?.map(({ id, title, count }) => ({
                  id,
                  count,
                  title,
                  isSelected: isInGenderFilters(id),
                  onSelect: () =>
                    toggleFilterAndSetUrl(id, undefined, FilterQueryItem.GENDER)
                })) ?? []
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    !isParfumCampaign && categories && categories.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="category"
        dropdownKey="category"
        filterType={FilterQueryItem.CATEGORY}
        isOpen={openDropdownKey === 'category'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'category' ? null : 'category'
          )
        }
        title={t('Category.Filter.category')}
        data-testid="filterCategory"
      >
        <DropdownMenuItemsGrid>
          <DropDownGridItem
            key="categoriesgrid"
            typeDropDownItem={FilterQueryItem.CATEGORY}
            onDiscard={onDiscardAllFilter(
              FilterQueryItem.CATEGORY,
              activeFilters.categories
            )}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={categories.map(({ title, items }) => ({
              title,
              items:
                items?.map(({ id, title, count }) => ({
                  id,
                  count,
                  title,
                  isSelected: isInCategoryFilters(id),
                  onSelect: () =>
                    toggleFilterAndSetUrl(
                      id,
                      undefined,
                      FilterQueryItem.CATEGORY
                    )
                })) ?? []
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    sizes && sizes.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="sizes"
        dropdownKey="sizes"
        filterType={FilterQueryItem.SIZE}
        isOpen={openDropdownKey === 'sizes'}
        onToggle={() =>
          setOpenDropdownKey((prev) => (prev === 'sizes' ? null : 'sizes'))
        }
        title={t('Category.size')}
        data-testid="filterSize"
      >
        <DropdownMenuItemsGrid key="sizes">
          <DropDownGridItem
            typeDropDownItem={FilterQueryItem.SIZE}
            key="sizes+grid"
            onDiscard={onDiscardAllFilter(
              FilterQueryItem.SIZE,
              activeFilters.sizes
            )}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={sizes.map(({ title, items }) => ({
              title,
              items: items.map(({ id, title, count, englishName }) => ({
                id,
                count,
                title,
                isSelected: isInSizeFilters(id),
                onSelect: () =>
                  toggleFilterAndSetUrl(id, englishName, FilterQueryItem.SIZE)
              }))
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    colors && colors.length > 0 && !isParfumCampaign && (
      <DropdownItemWithArrowRedesign
        key="colors"
        dropdownKey="colors"
        filterType={FilterQueryItem.COLOR}
        isOpen={openDropdownKey === 'colors'}
        onToggle={() =>
          setOpenDropdownKey((prev) => (prev === 'colors' ? null : 'colors'))
        }
        title={t('Category.Filter.color')}
        data-testid="filterColor"
      >
        <DropdownMenuItemsGrid>
          <DropDownGridItem
            key="filterColor+grid"
            typeDropDownItem={FilterQueryItem.COLOR}
            onDiscard={onDiscardAllFilter(
              FilterQueryItem.COLOR,
              activeFilters.colors
            )}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={colors.map(({ title, items }) => ({
              title,
              items: items.map(({ id, value, count, title, englishName }) => ({
                id,
                count,
                title: title
                  ? title.charAt(0).toUpperCase() + title.slice(1)
                  : '',
                color: value,
                isSelected: isInColorFilters(id),
                onSelect: () =>
                  toggleFilterAndSetUrl(id, englishName, FilterQueryItem.COLOR)
              }))
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    materials && materials.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="material"
        dropdownKey="material"
        filterType={FilterQueryItem.MATERIAL}
        isOpen={openDropdownKey === 'material'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'material' ? null : 'material'
          )
        }
        title={t('Category.Filter.material')}
        data-testid="filterMaterial"
      >
        <DropdownMenuItemsGrid>
          <DropDownGridItem
            key="filterMaterial+grid"
            typeDropDownItem={FilterQueryItem.MATERIAL}
            onDiscard={onDiscardAllFilter(
              FilterQueryItem.MATERIAL,
              activeFilters.materials
            )}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={materials.map(({ title, items }) => ({
              title,
              items: items.map(({ id, count, title, englishName }) => ({
                id,
                count,
                title,
                isSelected: isInMaterialFilters(id),
                onSelect: () =>
                  toggleFilterAndSetUrl(
                    id,
                    englishName,
                    FilterQueryItem.MATERIAL
                  )
              }))
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    manufacturers && manufacturers.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="manufacturers"
        dropdownKey="manufacturers"
        filterType={FilterQueryItem.MANUFACTURER}
        isOpen={openDropdownKey === 'manufacturers'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'manufacturers' ? null : 'manufacturers'
          )
        }
        title={t('Category.Filter.manufacturer')}
        data-testid="filterManufacturer"
      >
        <ManufacturerDesktopFilter
          manufacturers={manufacturers}
          onManufacturerChange={(id, englishName) =>
            toggleFilterAndSetUrl(id, englishName, FilterQueryItem.MANUFACTURER)
          }
          isInActiveFilters={isInManufacturerFilters}
          onDiscardFilters={onDiscardAllFilter(
            FilterQueryItem.MANUFACTURER,
            activeFilters.manufacturers
          )}
          onConfirm={(selectedIds, type) =>
            setFiltersForType(type as FilterQueryItem, selectedIds)
          }
          onClose={() => setOpenDropdownKey(null)}
        />
      </DropdownItemWithArrowRedesign>
    ),

    priceRange && (
      <DropdownItemWithArrowRedesign
        key="priceRange"
        dropdownKey="priceRange"
        filterType={FilterQueryItem.PRICE_MAX || FilterQueryItem.PRICE_MIN}
        isOpen={openDropdownKey === 'priceRange'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'priceRange' ? null : 'priceRange'
          )
        }
        title={t('Category.Filter.price')}
        data-testid="filterPrice"
      >
        <PriceFilterRedesign
          width={260}
          priceRange={priceRange}
          onClose={() => setOpenDropdownKey(null)}
        />
      </DropdownItemWithArrowRedesign>
    ),

    patterns && patterns.length > 0 && (
      <DropdownItemWithArrowRedesign
        key="patterns"
        dropdownKey="patterns"
        filterType={FilterQueryItem.PATTERN}
        isOpen={openDropdownKey === 'patterns'}
        onToggle={() =>
          setOpenDropdownKey((prev) =>
            prev === 'patterns' ? null : 'patterns'
          )
        }
        title={t('Category.Filter.motive')}
        data-testid="filterMotives"
      >
        <DropdownMenuItemsGrid>
          <DropDownGridItem
            key="filterMotive+grid"
            typeDropDownItem={FilterQueryItem.PATTERN}
            onDiscard={() => discardAllFilter(FilterQueryItem.PATTERN)}
            onConfirm={(selectedIds, type) =>
              setFiltersForType(type as FilterQueryItem, selectedIds)
            }
            onClose={() => setOpenDropdownKey(null)}
            sections={patterns.map(({ title, items }) => ({
              title,
              items: items.map(({ id, count, title, englishName }) => ({
                id,
                count,
                title,
                englishName,
                isSelected: isInPatternFilters(id),
                onSelect: () =>
                  toggleFilterAndSetUrl(
                    id,
                    englishName,
                    FilterQueryItem.PATTERN
                  )
              }))
            }))}
          />
        </DropdownMenuItemsGrid>
      </DropdownItemWithArrowRedesign>
    ),

    ...(tags?.length
      ? (() => {
          const activeTags = tags.filter((section) =>
            section.items.some((item) => isInTagFilters(section.id, item.id))
          )
          const inactiveTags = tags.filter(
            (section) =>
              !section.items.some((item) => isInTagFilters(section.id, item.id))
          )
          return [...activeTags, ...inactiveTags].map(
            ({ id: tagSectionId, title: sectionTitle, items }) => (
              <DropdownItemWithArrowRedesign
                key={tagSectionId}
                dropdownKey={tagSectionId}
                filterType={FilterQueryItem.TAG}
                parentId={tagSectionId}
                isOpen={openDropdownKey === tagSectionId || false}
                onToggle={() =>
                  setOpenDropdownKey((prev) =>
                    prev === tagSectionId ? null : tagSectionId
                  )
                }
                title={sectionTitle}
                highlight={false}
                height="100%"
                data-testid="filterTag"
              >
                <DropdownMenuItemsGrid>
                  <DropDownGridItem
                    key={`filterTag+grid-${tagSectionId}`}
                    typeDropDownItem={FilterQueryItem.TAG}
                    onDiscard={() =>
                      discardAllFilter(
                        FilterQueryItem.TAG.replace('{tagId}', tagSectionId)
                      )
                    }
                    onConfirm={(selectedIds, type) =>
                      setFiltersForType(
                        type as FilterQueryItem,
                        selectedIds,
                        tagSectionId,
                        sectionTitle
                      )
                    }
                    onClose={() => setOpenDropdownKey(null)}
                    sections={[
                      {
                        items: items.map(
                          ({ id, title, count, englishName }) => ({
                            id,
                            count,
                            title,
                            isSelected: isInTagFilters(tagSectionId, id),
                            onSelect: () =>
                              toggleFilterAndSetUrl(
                                id,
                                englishName,
                                FilterQueryItem.TAG,
                                tagSectionId,
                                sectionTitle
                              )
                          })
                        )
                      }
                    ]}
                  />
                </DropdownMenuItemsGrid>
              </DropdownItemWithArrowRedesign>
            )
          )
        })()
      : [])
  ].filter(Boolean)

  const [showAllFilters, setShowAllFilters] = useState<boolean>(false)
  const maxVisibleFilters = 8

  const visibleFilters = showAllFilters
    ? allFilters
    : allFilters.slice(0, maxVisibleFilters)

  return (
    <Box data-testid="desktopFilter" {...rest}>
      <FiltersWrapper>
        <StyledFlex>
          {visibleFilters.map((filterComponent, index) => (
            <Fragment key={index}>{filterComponent}</Fragment>
          ))}
          {allFilters.length > maxVisibleFilters && (
            <StyledIconButton
              onClick={() => setShowAllFilters((prev) => !prev)}
              title={
                showAllFilters
                  ? t('Category.Filter.showLessFilters')
                  : t('Category.Filter.showAllFilters')
              }
              IconElement={<FilterIconRedesignDefault width={12} height={12} />}
            />
          )}
        </StyledFlex>
        {activeFilterscount > 0 && (
          <StyledIconButton
            title={t('Category.Filter.cancelFilterButtonTitle')}
            IconElement={<ResetIconDefault width={12} height={12} />}
            onClick={() => discardAllFilter()}
            data-testid="cancelFilterButton"
          />
        )}
      </FiltersWrapper>
    </Box>
  )
}

export default DesktopFilter

type SortItemProps = {
  isSelected: boolean
}

const SortItem = styled<SortItemProps>(Small)`
  padding: 8px 12px;
  text-align: left;
  box-sizing: border-box;
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  height: 33px;
  background-color: ${(props) =>
    props.isSelected
      ? props.theme.colors.navMenuGrey
      : props.theme.colors.background};
  text-transform: lowercase;
  &::first-letter {
    text-transform: uppercase;
  }
  &:hover {
    cursor: pointer;
    background-color: ${(props) => props.theme.colors.navMenuGrey};
    transition: background-color 0.3s;
  }
`

const FiltersWrapper = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  gap: 32px;
`

const StyledFlex = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 92%;
`

const StyledIconButton = styled(IconButton)`
  padding: 8px 12px;
  box-sizing: border-box;
  border-color: ${(props) => props.theme.colors.buttonBorderNew};
  border-radius: 4px;
  border-width: 1.5px;
  min-width: fit-content;
  height: 33px;
  display: flex;
  align-items: center;
  gap: 8px;

  p {
    margin: 0;
    font-size: 14px;
  }
`
